import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronRightIcon, LogOutIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useSignedInAccount } from '../../contexts/AccountContext';
import { paymentOptions } from '../../data/payments';
import { Spinner } from '../../ui/Spinner';
import { btnPrimary, btnQuiet, btnSecondary } from '../../ui/styles';
import { accountHeading } from '../../ui/sections';
import { ease } from '../../core/format/motion';
import { formatStreet } from '../../core/rules/contact';
import type { Customer } from '../../core/types/account';
import { FormError } from '../shared/FormError';
import { PageTitle } from './PageTitle';
import { Switch } from './Switch';
import { ProfileTextField } from './profile/ProfileTextField';
import { DataRights } from './profile/DataRights';
import { PasswordSection } from './profile/PasswordSection';
import { NewsletterPref } from './profile/NewsletterPref';
import { BirthdayFields } from './profile/BirthdayFields';
import { birthdayText, patchFor, profileErrors } from './profile/profileRules';
import type { ProfileSection } from './profile/profileRules';
type Row = {
  key: ProfileSection;
  title: string;
  value: string;
  fields: ReactNode;
};

// De nieuwsbrief staat apart (NewsletterPref): aanmelden gaat met een bevestigingsmail, niet met een schakelaar.
const prefs: {
  key: keyof Customer['prefs'];
  label: string;
  hint: string;
}[] = [{
  key: 'refillWhatsApp',
  label: 'Seintje via WhatsApp als iets bijna op is',
  hint: 'Eén bericht per product. Geen reclame.'
}, {
  key: 'refillEmail',
  label: 'Hetzelfde seintje per mail',
  hint: 'Als je liever geen WhatsApp krijgt.'
}];

/** Zo staan de regels gegroepeerd: wie je bent, en waar het heen gaat en hoe je betaalt. */
const groups: {
  id: string;
  title: string;
  keys: ProfileSection[];
}[] = [{
  id: 'over-jou',
  title: 'Over jou',
  keys: ['persoon', 'verjaardag']
}, {
  id: 'bezorging',
  title: 'Bezorging en betalen',
  keys: ['adres', 'betalen']
}];

/**
 * Ik — alles wat je zelf wilt kunnen wijzigen, niets meer. In groepen met dunne lijnen:
 * Over jou · Bezorging en betalen · Berichten · Inloggen · Privacy, en onderaan uitloggen.
 * Wie een open veld wijzigt en een ander onderdeel opent, krijgt eerst de vraag of het bewaard moet.
 */
export function Profile() {
  const {
    account,
    updateCustomer,
    saveProfile,
    signOut
  } = useSignedInAccount();
  const reduce = useReducedMotion();
  const c = account.customer;
  const [open, setOpen] = useState<ProfileSection | null>(null);
  const [pending, setPending] = useState<ProfileSection | null>(null);
  const [form, setForm] = useState<Customer>(c);
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  /** 423 `reauth_required` bij een ander e-mailadres: opnieuw inloggen, daarna nog eens bewaren. */
  const [reauth, setReauth] = useState(false);
  const dirty = open !== null && JSON.stringify(patchFor(open, form)) !== JSON.stringify(patchFor(open, c));

  // Tabblad sluiten of herladen met onbewaarde wijzigingen: de browser vraagt het even.
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
  const openRow = (s: ProfileSection | null) => {
    setForm(account.customer);
    setErrors({});
    setSaveError(null);
    setReauth(false);
    setPending(null);
    setOpen(s);
  };
  const edit = (s: ProfileSection) => {
    if (dirty && open !== s) {
      setPending(s);
      return;
    }
    openRow(s);
  };
  const save = async (then: ProfileSection | null = null) => {
    if (!open || saving) return;
    const found = profileErrors(open, form);
    setErrors(found);
    const firstBad = Object.keys(found)[0];
    if (firstBad) {
      setPending(null);
      document.getElementById(`profile-${firstBad}`)?.focus();
      return;
    }
    setSaving(true);
    setSaveError(null);
    setReauth(false);
    const res = await saveProfile(patchFor(open, form));
    setSaving(false);
    if (!res.ok) {
      setPending(null);
      // Ander e-mailadres zonder recente login: dezelfde uitweg als bij het wachtwoord.
      if (res.code === 'reauth_required') {
        setReauth(true);
        setSaveError(res.error);
        return;
      }
      const fieldErrors = res.fields ? Object.fromEntries(Object.entries(res.fields).filter(([k]) => k in form || k in form.address)) : {};
      if (Object.keys(fieldErrors).length > 0) {
        setErrors(fieldErrors);
        document.getElementById(`profile-${Object.keys(fieldErrors)[0]}`)?.focus();
      } else setSaveError(res.error);
      return;
    }
    toast.success('Opgeslagen');
    openRow(then);
  };
  const setPref = (key: keyof Customer['prefs']) => void updateCustomer({
    prefs: {
      ...c.prefs,
      [key]: !c.prefs[key]
    }
  });
  const setAddress = (patch: Partial<Customer['address']>) => setForm({
    ...form,
    address: {
      ...form.address,
      ...patch
    }
  });
  const rows: Row[] = [{
    key: 'persoon',
    title: 'Naam en contact',
    value: [`${c.firstName} ${c.lastName}`.trim(), c.email, c.phone].filter(Boolean).join(' · '),
    fields: <div className="grid gap-4 sm:grid-cols-2" data-id="element-6481" data-mp="oes97i8gevpe">
          <ProfileTextField id="profile-firstName" label="Voornaam" value={form.firstName} onChange={v => setForm({
        ...form,
        firstName: v
      })} autoComplete="given-name" error={errors.firstName} data-id="element-6482" data-mp="jmqzo42o7j32" />
          <ProfileTextField id="profile-lastName" label="Achternaam" value={form.lastName} onChange={v => setForm({
        ...form,
        lastName: v
      })} autoComplete="family-name" data-id="element-6483" data-mp="9ol19wg2ov2y" />
          <ProfileTextField id="profile-email" label="E-mailadres" type="email" inputMode="email" value={form.email} onChange={v => setForm({
        ...form,
        email: v
      })} autoComplete="email" error={errors.email} data-id="element-6484" data-mp="5osr5fb16gvh" />
          <ProfileTextField id="profile-phone" label="Mobiel (voor WhatsApp)" type="tel" inputMode="tel" value={form.phone} onChange={v => setForm({
        ...form,
        phone: v
      })} autoComplete="tel" data-id="element-6485" data-mp="3d77eost1z91b" />
        </div>
  }, {
    key: 'verjaardag',
    title: 'Verjaardag',
    value: birthdayText(c.birthday),
    fields: <BirthdayFields value={form.birthday ?? null} onChange={birthday => setForm({
      ...form,
      birthday
    })} error={errors.birthday} data-id="element-6486" data-mp="epv189axq2mo" />
  }, {
    key: 'adres',
    title: 'Bezorgadres',
    value: c.address.street ? `${formatStreet(c.address)}, ${c.address.postcode} ${c.address.city}`.trim() : 'Nog geen adres. Vul het in, dan hoef je het bij afrekenen niet meer te typen.',
    fields: <div className="grid gap-4 sm:grid-cols-2" data-id="element-6487" data-mp="xguw1r5426nv">
          <ProfileTextField id="profile-postcode" label="Postcode" value={form.address.postcode} onChange={v => setAddress({
        postcode: v
      })} autoComplete="postal-code" error={errors.postcode} data-id="element-6488" data-mp="2i6i3ogh22ixo" />
          <ProfileTextField id="profile-houseNumber" label="Huisnummer" value={form.address.houseNumber} onChange={v => setAddress({
        houseNumber: v
      })} autoComplete="address-line2" error={errors.houseNumber} data-id="element-6489" data-mp="2qysg84ffb0i3" />
          <ProfileTextField id="profile-street" label="Straat" value={form.address.street} onChange={v => setAddress({
        street: v
      })} autoComplete="address-line1" error={errors.street} data-id="element-6490" data-mp="1z7cppzaqwtml" />
          <ProfileTextField id="profile-city" label="Plaats" value={form.address.city} onChange={v => setAddress({
        city: v
      })} autoComplete="address-level2" error={errors.city} data-id="element-6491" data-mp="288apg5e5iezd" />
        </div>
  }, {
    key: 'betalen',
    title: account.subscription ? 'Betaalmethode voor je abonnement' : 'Voorkeur bij afrekenen',
    value: c.payment.label,
    fields: <fieldset data-id="element-6492" data-mp="3eqqv0ye2zdzz">
          <legend className="sr-only" data-id="element-6493" data-mp="2ld2920s8uvc">Betaalmethode</legend>
          <div className="grid gap-2 sm:grid-cols-3" data-id="element-6494" data-mp="2mxwsrn3kdt7">
            {paymentOptions.map(p => {
          const on = form.payment.method === p.method;
          return <button key={p.method} type="button" aria-pressed={on} onClick={() => setForm({
            ...form,
            payment: {
              method: p.method,
              label: p.label
            }
          })} className={`min-w-0 px-4 py-3 text-left text-[14px] transition-[background-color,color,box-shadow] duration-150 ease-soft ${on ? 'bg-ink text-shell' : 'bg-shell text-ink ring-1 ring-line hover:ring-ink/40'}`} data-id="element-6495" data-mp="bkzw92r6myed">
                  <span className="block" data-id="element-6496" data-mp="zakqihssdi2y">{p.label}</span>
                  {p.note && <span className={`mt-0.5 block text-[12px] leading-snug ${on ? 'text-shell/70' : 'text-muted'}`} data-id="element-6497" data-mp="2x26vodn91zic">{p.note}</span>}
                </button>;
        })}
          </div>
          <p className="mt-2 text-[13px] text-muted" data-id="element-6498" data-mp="2tjhlrumshgil">
            {account.subscription ? 'Bij je volgende levering bevestig je de nieuwe methode één keer via een betaallink.' : 'Staat bij afrekenen alvast voor je klaar. Je kunt daar altijd iets anders kiezen.'}
          </p>
        </fieldset>
  }];
  const byKey = new Map(rows.map(r => [r.key, r]));
  const pendingTitle = pending ? byKey.get(pending)?.title : undefined;
  return <div className="max-w-3xl" data-id="element-6499" data-mp="37j8sdaaz7umk">
      <PageTitle lead="Wat je hier invult, staat bij afrekenen en afspraken al voor je klaar." data-id="element-6500" data-mp="37k1z4b9xqk4p" />

      <div className="space-y-12" data-id="element-6501" data-mp="33gcchrctmcmb">
        {groups.map(g => <section key={g.id} aria-labelledby={`profile-group-${g.id}`} data-id="element-6502" data-mp="2yikmj2vql0kr">
            <h2 id={`profile-group-${g.id}`} className={accountHeading} data-id="element-6503" data-mp="2yknc5lsz8roy">
              {g.title}
            </h2>
            <ul className="mt-4 divide-y divide-line border-y border-line" data-id="element-6504" data-mp="1n35hdbtdj2ek">
              {g.keys.map(key => {
            const r = byKey.get(key);
            if (!r) return null;
            return <li key={r.key} className="py-5" data-id="element-6505" data-mp="2wx0x03ajtpwr">
                    <div className="flex items-start justify-between gap-4" data-id="element-6506" data-mp="2wueg3d1rpy1v">
                      <div className="min-w-0" data-id="element-6507" data-mp="3apgu6ngrqtpr">
                        <h3 id={`profile-row-${r.key}`} className="text-[13px] font-normal text-muted" data-id="element-6508" data-mp="35xfm7dqmvp5j">
                          {r.title}
                        </h3>
                        <p className="mt-0.5 break-words text-[15px] text-ink" data-id="element-6509" data-mp="3ko6e0ls86o8n">{r.value}</p>
                      </div>
                      {open !== r.key && <button type="button" onClick={() => edit(r.key)} aria-label={`${r.title} wijzigen`} className={`${btnQuiet} -mr-3 shrink-0`} data-id="element-6510" data-mp="3tlqi02hh2ymy">
                          Wijzig
                        </button>}
                    </div>
                    <AnimatePresence initial={false} data-id="element-6511" data-mp="1v7f1h7orpws6">
                      {open === r.key && <motion.div initial={reduce ? {
                  opacity: 0
                } : {
                  height: 0,
                  opacity: 0
                }} animate={reduce ? {
                  opacity: 1
                } : {
                  height: 'auto',
                  opacity: 1
                }} exit={reduce ? {
                  opacity: 0
                } : {
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.22,
                  ease
                }} className="overflow-hidden" data-id="element-6512" data-mp="1l3gt673eigbl">
                          <form className="pt-5" aria-labelledby={`profile-row-${r.key}`} noValidate onSubmit={e => {
                    e.preventDefault();
                    void save();
                  }} data-id="element-6513" data-mp="11do7v5v3s4sw">
                            {r.fields}
                            {reauth && saveError ? <div role="alert" className="mt-5 border border-clay/40 bg-blush px-4 py-3 text-[14px] leading-relaxed text-ink" data-id="element-6514" data-mp="2rrx2trc7f9ty">
                                <p data-id="element-6515" data-mp="2paw2i9xvwj8l">{saveError}</p>
                                <p className="mt-1 text-muted" data-id="element-6516" data-mp="2lb5daao0ab66">Daarna kun je het nieuwe adres meteen opnieuw bewaren.</p>
                                <button type="button" onClick={signOut} className={`${btnPrimary} mt-3`} data-id="element-6517" data-mp="2gd3ln1cil3j3">
                                  Opnieuw inloggen
                                </button>
                              </div> : <FormError message={saveError} className="mt-5" data-id="element-6518" data-mp="341d35r2p354f" />}
                            {pending && <div role="alert" className="mt-5 bg-blush p-4" data-id="element-6519" data-mp="2z9bqi41m7cdv">
                                <p className="text-[14px] text-ink" data-id="element-6520" data-mp="2ubk2hl43caey">Je wijzigingen zijn nog niet bewaard. Eerst bewaren{pendingTitle ? `, dan naar ${pendingTitle.toLowerCase()}` : ''}?</p>
                                <div className="mt-3 flex flex-wrap gap-2" data-id="element-6521" data-mp="3mzpla2bl3pc5">
                                  <button type="button" disabled={saving} onClick={() => void save(pending)} className={btnPrimary} data-id="element-6522" data-mp="3ucf2f5uz48wz">
                                    {saving && <Spinner data-id="element-6523" data-mp="35ujk9f2o7b1" />}
                                    Bewaren
                                  </button>
                                  <button type="button" disabled={saving} onClick={() => openRow(pending)} className={btnSecondary} data-id="element-6524" data-mp="d3s2che5xbw9">
                                    Niet bewaren
                                  </button>
                                </div>
                              </div>}
                            {!pending && <div className="mt-5 flex gap-2" data-id="element-6525" data-mp="qx9p0b25b4qi">
                                <button type="submit" disabled={saving} className={btnPrimary} data-id="element-6526" data-mp="vt87xj4pfujj">
                                  {saving && <Spinner data-id="element-6527" data-mp="10unr15nbnqza" />}
                                  {saving ? 'Bezig…' : 'Opslaan'}
                                </button>
                                <button type="button" onClick={() => openRow(null)} className={btnQuiet} data-id="element-6528" data-mp="22jue9ybexbwv">
                                  Annuleren
                                </button>
                              </div>}
                          </form>
                        </motion.div>}
                    </AnimatePresence>
                  </li>;
          })}
            </ul>
            {/* Facturen zoeken mensen vaak bij hun gegevens; één tik naar de plek waar ze staan. */}
            {g.id === 'bezorging' && account.orders.length > 0 && <Link to="/account/bestellingen" className="mt-3 inline-flex min-h-[44px] items-center gap-1 text-[14px] text-muted transition-colors duration-150 ease-soft hover:text-ink" data-id="element-6529" data-mp="1slwsrikxtt7y">
                Bestellingen en facturen
                <ChevronRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-6530" data-mp="33z8r5dxxhjmq" />
              </Link>}
          </section>)}

        <section aria-labelledby="prefs-title" data-id="element-6531" data-mp="37i7vpxudoxyi">
          <h2 id="prefs-title" className={accountHeading} data-id="element-6532" data-mp="37hfxef9ecm4j">
            Berichten
          </h2>
          <ul className="mt-4 divide-y divide-line border-y border-line" data-id="element-6533" data-mp="3blvzs6jsxbf0">
            {prefs.map(p => <li key={p.key} data-id="element-6534" data-mp="2xmec5ui8s478">
                <Switch checked={c.prefs[p.key]} onChange={() => setPref(p.key)} label={p.label} hint={p.hint} className="py-4" data-id="element-6535" data-mp="32jef3wjsoqu4" />
              </li>)}
            <li data-id="element-6536" data-mp="3qa0mdllz6732">
              <NewsletterPref email={c.email} subscribed={c.prefs.newsletter} onUnsubscribe={() => setPref('newsletter')} data-id="element-6537" data-mp="3qbl8cmu84hpb" />
            </li>
          </ul>
        </section>
      </div>

      <PasswordSection data-id="element-6538" data-mp="3gj5rxtyks16j" />

      <DataRights data-id="element-6539" data-mp="3liil0wg1i4hk" />

      {/* Op de computer staat uitloggen ook onderaan het zijmenu; op de telefoon alleen hier. */}
      <div className="mt-12 border-t border-line pt-6 lg:hidden" data-id="element-6540" data-mp="d2702xow78hi">
        <button type="button" onClick={signOut} className={`${btnQuiet} -ml-3`} data-id="element-6541" data-mp="d0vvwwrjbypv">
          <LogOutIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-6542" data-mp="huqf7z38abof" />
          Uitloggen
        </button>
      </div>
    </div>;
}